import { IconButton } from '@atlaskit/button/new';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { messages } from '@databases/intl/messages';
import { isUniqueFieldName } from '@databases/utils/field-name';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  type RefObject,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { EditFieldDescriptionInput } from './edit-field-description-input.tsx';
import styles from './field-menu-header.module.css';

const containerStyles = xcss({
  paddingBlockStart: 'space.150',
  paddingInline: 'space.150',
  paddingBlockEnd: 'space.0'
});

const editFieldContainerStyles = xcss({
  borderTop: `1px solid ${token('color.border')}`,
  paddingBlockStart: 'space.200',
  paddingInline: 'space.200',
  paddingBlockEnd: 'space.0'
});

const errorStyles = xcss({
  width: '224px'
});

const editFieldErrorStyles = xcss({
  width: '100%'
});

const errorContentStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  columnGap: 'space.050'
});

const errorTextStyles = xcss({
  overflowWrap: 'anywhere'
});

const infoIconWrapperStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  paddingInlineEnd: 'space.100'
});

const infoIconStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px'
});

const DESCRIPTION_MAX_LENGTH = 2000;
const FIELD_MENU_TEXT_BOX_STYLE: React.CSSProperties = {
  width: '224px',
  height: '32px',
  paddingLeft: token('space.100'),
  paddingRight: token('space.100'),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};
const FIELD_MENU_DESCRIPTION_TEXT_AREA_STYLE: React.CSSProperties = {
  width: '224px'
};

export type FieldMenuHeaderHandle = {
  save: () => boolean;
};

type FieldMenuHeaderProps = {
  name: string;
  description?: string;
  existingFieldNames: string[];
  onSaveName: (name: string) => void;
  onSaveDescription: (description: string) => void;
  onRequestClose: () => void;
  nameInputRef?: RefObject<HTMLInputElement | null>;
  variant?: 'contextual-menu-header' | 'edit-field';
};

const FieldMenuHeader = forwardRef<FieldMenuHeaderHandle, FieldMenuHeaderProps>(
  function FieldMenuHeader(
    {
      name: originalName,
      description: originalDescription = '',
      existingFieldNames,
      onSaveName,
      onSaveDescription,
      onRequestClose,
      nameInputRef,
      variant = 'contextual-menu-header'
    },
    ref
  ) {
    const { formatMessage } = useIntl();
    const [name, setName] = useState(originalName);
    const savedNameRef = useRef(originalName);
    const [description, setDescription] = useState(originalDescription);
    const descriptionRef = useRef(originalDescription);
    const savedDescriptionRef = useRef(originalDescription);
    const [isDescriptionVisible, setIsDescriptionVisible] = useState(
      originalDescription.length > 0
    );
    const [autoFocusDescription, setAutoFocusDescription] = useState(false);
    const isEditField = variant === 'edit-field';
    const isNameUnique = useMemo(
      () => isUniqueFieldName(name, existingFieldNames),
      [existingFieldNames, name]
    );

    useEffect(() => {
      if (isEditField) {
        nameInputRef?.current?.select();
      }
    }, [isEditField, nameInputRef]);

    const saveDescription = useCallback(() => {
      const nextDescription = descriptionRef.current;

      if (nextDescription !== savedDescriptionRef.current) {
        onSaveDescription(nextDescription);
        savedDescriptionRef.current = nextDescription;
      }
    }, [onSaveDescription]);

    const handleDescriptionChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        descriptionRef.current = event.currentTarget.value;
        setDescription(event.currentTarget.value);
      },
      []
    );

    const save = useCallback(() => {
      if (!isNameUnique) {
        return false;
      }

      const nextName = name.trim() || originalName;
      setName(nextName);
      if (nextName !== savedNameRef.current) {
        savedNameRef.current = nextName;
        onSaveName(nextName);
      }
      saveDescription();
      return true;
    }, [isNameUnique, name, onSaveName, originalName, saveDescription]);

    useImperativeHandle(ref, () => ({ save }), [save]);

    const cancel = useCallback(() => {
      setName(originalName);
      setDescription(originalDescription);
      descriptionRef.current = originalDescription;
      onRequestClose();
    }, [onRequestClose, originalDescription, originalName]);

    const handleNameKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          if (save()) {
            event.currentTarget.blur();
          }
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          cancel();
        }
      },
      [cancel, save]
    );

    const handleDescriptionKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          if (save()) {
            event.currentTarget.blur();
            onRequestClose();
          }
        }
      },
      [onRequestClose, save]
    );

    return (
      <Box
        xcss={isEditField ? editFieldContainerStyles : containerStyles}
        testId="field-menu-header"
      >
        <Stack space="space.075">
          {isEditField && (
            <Text color="color.text.subtle" size="small" weight="bold">
              {formatMessage(messages['field-settings.name.non-final'])}
            </Text>
          )}
          <Textfield
            ref={nameInputRef}
            autoFocus
            autoComplete="off"
            className={isEditField ? styles.fieldMenuNameInputContainer : undefined}
            testId="field-menu-name-input"
            value={name}
            isInvalid={!isNameUnique}
            style={
              isEditField
                ? { ...FIELD_MENU_TEXT_BOX_STYLE, width: '100%', boxSizing: 'border-box' }
                : FIELD_MENU_TEXT_BOX_STYLE
            }
            aria-label={formatMessage(messages['field-settings.field-name'])}
            onChange={(event) => setName(event.currentTarget.value)}
            onKeyDown={handleNameKeyDown}
            elemAfterInput={
              <Box xcss={infoIconStyles}>
                {!isDescriptionVisible && (
                  <Tooltip
                    content={formatMessage(messages['field-settings.field-description'])}
                  >
                    <Box xcss={infoIconWrapperStyles}>
                      <IconButton
                        appearance="subtle"
                        spacing="compact"
                        testId="field-menu-description-button"
                        label={formatMessage(
                          messages['field-settings.field-description']
                        )}
                        icon={(iconProps) => (
                          <StatusInformationIcon
                            {...iconProps}
                            size="medium"
                            color={token('color.icon.subtle')}
                          />
                        )}
                        onClick={() => {
                          setIsDescriptionVisible(true);
                          setAutoFocusDescription(true);
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            }
          />
          {!isNameUnique && (
            <Box xcss={isEditField ? editFieldErrorStyles : errorStyles}>
              <Box xcss={errorContentStyles}>
                <StatusErrorIcon
                  label=""
                  color={token('color.icon.danger')}
                  size="small"
                />
                <Box xcss={errorTextStyles}>
                  <Text color="color.text.danger" size="small">
                    {formatMessage(messages['field-context-menu.duplicate-field-name'], {
                      fieldName: name
                    })}
                  </Text>
                </Box>
              </Box>
            </Box>
          )}
          {isDescriptionVisible && isEditField && (
            <EditFieldDescriptionInput
              autoFocus={autoFocusDescription}
              value={description}
              ariaLabel={formatMessage(
                messages['field-settings.field-description.label']
              )}
              placeholder={formatMessage(
                messages['field-context-menu.description-placeholder']
              )}
              onChange={handleDescriptionChange}
              onBlur={saveDescription}
              onKeyDown={handleDescriptionKeyDown}
            />
          )}
          {isDescriptionVisible && !isEditField && (
            <TextArea
              autoFocus={autoFocusDescription}
              testId="field-menu-description-input"
              value={description}
              maxLength={DESCRIPTION_MAX_LENGTH}
              minimumRows={1}
              style={FIELD_MENU_DESCRIPTION_TEXT_AREA_STYLE}
              aria-label={formatMessage(
                messages['field-settings.field-description.label']
              )}
              placeholder={formatMessage(
                messages['field-context-menu.description-placeholder']
              )}
              onChange={handleDescriptionChange}
              onBlur={saveDescription}
              onKeyDown={handleDescriptionKeyDown}
            />
          )}
        </Stack>
      </Box>
    );
  }
);

export default FieldMenuHeader;
